import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';

function readInnerHeight() {
  if (typeof window === 'undefined') {
    return 800;
  }
  if (typeof window.innerHeight === 'number') {
    return window.innerHeight;
  }
  return 800;
}

function readInnerWidth() {
  if (typeof window === 'undefined') {
    return 400;
  }
  if (typeof window.innerWidth === 'number') {
    return window.innerWidth;
  }
  return 400;
}

export function useBodyAnchoredDropdown(rootRef, isOpenRef, options) {
  const margin = options !== undefined && options !== null && typeof options.margin === 'number' ? options.margin : 8;
  const panelOffset = options !== undefined && options !== null && typeof options.panelOffset === 'number' ? options.panelOffset : margin;
  const zIndex = options !== undefined && options !== null && typeof options.zIndex === 'number' ? options.zIndex : 265;
  const capHeightDefault = options !== undefined && options !== null && typeof options.capHeight === 'number' ? options.capHeight : 240;
  const minHeight = options !== undefined && options !== null && typeof options.minHeight === 'number' ? options.minHeight : 120;
  const flipBelow = options !== undefined && options !== null && typeof options.flipBelow === 'number' ? options.flipBelow : 160;
  const preferAboveWhenFits = options !== undefined && options !== null && options.preferAboveWhenFits === true;
  const panelRef = options !== undefined && options !== null && options.panelRef !== undefined ? options.panelRef : null;

  function resolveCapHeight() {
    if (options !== undefined && options !== null && typeof options.resolveCapHeight === 'function') {
      const v = options.resolveCapHeight();
      if (typeof v === 'number' && Number.isFinite(v) === true) {
        return v;
      }
    }
    return capHeightDefault;
  }

  function resolveDesiredHeight(capHeight) {
    if (panelRef !== null && panelRef.value !== null) {
      const naturalHeight = panelRef.value.scrollHeight;
      if (typeof naturalHeight === 'number' && Number.isFinite(naturalHeight) === true && naturalHeight > 0) {
        return Math.min(capHeight, Math.max(minHeight, naturalHeight));
      }
    }
    return capHeight;
  }

  const layout = ref({
    top: '0px',
    left: '0px',
    width: '280px',
    maxHeight: `${capHeightDefault}px`
  });

  const panelStyle = computed(() => ({
    position: 'fixed',
    zIndex,
    top: layout.value.top,
    left: layout.value.left,
    width: layout.value.width,
    maxHeight: layout.value.maxHeight
  }));

  function updatePosition() {
    if (rootRef.value === null) {
      return;
    }
    if (isOpenRef.value !== true) {
      return;
    }
    const rect = rootRef.value.getBoundingClientRect();
    const vh = readInnerHeight();
    const vw = readInnerWidth();
    const spaceBelow = vh - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const capHeight = resolveCapHeight();
    const desiredHeight = resolveDesiredHeight(capHeight);
    const canFitBelow = spaceBelow >= desiredHeight;
    const canFitAbove = spaceAbove >= desiredHeight;
    let topPx;
    let maxHPx;
    if (preferAboveWhenFits === true && canFitAbove === true) {
      maxHPx = desiredHeight;
      topPx = rect.top - desiredHeight - panelOffset;
    } else if (canFitBelow === true) {
      topPx = rect.bottom + panelOffset;
      maxHPx = desiredHeight;
    } else if (spaceBelow >= flipBelow || spaceBelow >= spaceAbove) {
      topPx = rect.bottom + panelOffset;
      maxHPx = Math.min(capHeight, Math.max(minHeight, spaceBelow - 4));
    } else {
      maxHPx = canFitAbove === true ? desiredHeight : Math.min(capHeight, Math.max(minHeight, spaceAbove - 4));
      topPx = rect.top - maxHPx - panelOffset;
      if (topPx < margin) {
        topPx = margin;
        maxHPx = Math.max(minHeight, rect.top - margin * 2);
      }
    }
    let leftPx = rect.left;
    let widthPx = rect.width;
    if (leftPx < margin) {
      leftPx = margin;
    }
    if (leftPx + widthPx > vw - margin) {
      leftPx = Math.max(margin, vw - widthPx - margin);
    }
    if (widthPx > vw - margin * 2) {
      widthPx = Math.max(200, vw - margin * 2);
    }
    layout.value = {
      top: `${Math.round(topPx)}px`,
      left: `${Math.round(leftPx)}px`,
      width: `${Math.round(widthPx)}px`,
      maxHeight: `${Math.round(maxHPx)}px`
    };
  }

  function bindListeners() {
    window.addEventListener('resize', updatePosition);
    document.addEventListener('scroll', updatePosition, true);
  }

  function unbindListeners() {
    window.removeEventListener('resize', updatePosition);
    document.removeEventListener('scroll', updatePosition, true);
  }

  watch(isOpenRef, (open) => {
    if (open === true) {
      nextTick(() => {
        updatePosition();
        bindListeners();
      });
      return;
    }
    unbindListeners();
  });

  onBeforeUnmount(() => {
    unbindListeners();
  });

  return { panelStyle, updatePosition };
}


